import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactPlayer from "react-player";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { fileIcons } from "../../../../constants/documentIcon";
import { getWheelId } from "../../../../redux/selectors/segmentBuilderSelectors";
import { getRecentMedia } from "../../../../services/api";
import { colors, tokens } from "../../../../utils";
import TextView from "../../../TextView";
import BrandWheelLoader from "../../../BrandWheelLoader";
import AudioBlock from "../AudioBlock";
import MediaMasonry from "../MediaBrowser/MediaMasonry";
import { goToUrl } from "../../../../utils/helpers";
import Dropdown from "../../../Dropdown";

// Styled Components
const MediaWrapper = styled.div`
  padding: 10px;
  height: 400px; 
  overflow-y: auto; 
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 32% 32% 32%;
  width: 100%;
  grid-gap: 2%;
`;

const DocumentItem = styled.div`
  border: 1px solid ${colors.lightBorder};
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 8px;
  height: 100%;
  overflow: hidden;
  position: relative;

  &:hover {
    border-color: ${colors.PrimaryPurple};
    border-width: 2px;
  }

  /* Reveal Select on hover AND keyboard focus (focus-within) so the control
     is reachable without a mouse (WCAG 2.1.1). */
  &:hover .select-button,
  &:focus-within .select-button {
    opacity: 1;
  }
`;

const DocumentIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const DocumentTitle = styled(TextView)`
  text-align: center;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  cursor: pointer;
`;

const NoMediaMessage = styled.div`
  color: ${colors.textSecondary};
  font-size: 14px;
  text-align: center;
  padding: 24px 12px;
`;

const EndOfResults = styled.div`
  color: ${colors.textSecondary};
  font-size: 12px;
  text-align: center;
  padding: 12px 0;
`;

const MediaContainer = styled.div`
  border-radius: 8px;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
  transition: border 0.3s ease;
  cursor: pointer;
  border: 2px solid;
  border-color: transparent;

  &:hover {
    border-color: ${colors.PrimaryPurple};
  }

  /* Reveal Select on hover AND keyboard focus (focus-within) so the control
     is reachable without a mouse (WCAG 2.1.1). */
  &:hover .select-button,
  &:focus-within .select-button {
    opacity: 1;
  }
`;

// T22.142: the player must not swallow the tile's click (a YouTube iframe or
// <video> would otherwise eat it), and the focused tile needs a focus ring.
const VideoTile = styled(MediaContainer)`
  & > :not(.select-button) {
    pointer-events: none;
  }

  &:focus-visible {
    outline: none;
    border-color: ${colors.PrimaryPurple};
    box-shadow: ${tokens.shadow.focus};
  }

  &:focus-visible .select-button {
    opacity: 1;
  }
`;

/*
 * ISP-0000 a11y (RecentMedia): Select was a hover-only `<div onClick>` with
 * `display:none` — invisible to keyboard users (a display:none node is not
 * focusable, so it could never be tabbed to or Enter-activated: WCAG 2.1.1
 * Keyboard) and unreachable on touch devices (no :hover to reveal it). It is
 * now a semantic <button>, kept in the tab order via opacity (never removed
 * from the accessibility tree), revealed on hover/focus, always shown where
 * there is no hover (touch), with a visible focus ring (WCAG 2.4.7).
 */
const SelectButton = styled.button`
  width: 60px;
  height: 30px;
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  border: none;
  padding: 0;
  font: inherit;
  /* DL-004 reach (ISP-4018): white-text fill -> on-fill token. */
  background-color: ${tokens.brand.primaryActive};
  color: white;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.3s ease, background-color 0.3s ease;
  z-index: 10000;

  &:hover {
    background-color: ${tokens.brand.primaryActiveHover};
  }

  &:focus-visible {
    opacity: 1;
    outline: none;
    box-shadow: ${tokens.shadow.focus};
  }

  /* Touch/coarse pointers have no hover — keep Select permanently reachable. */
  @media (hover: none) {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const getFileType = (type) => {
  switch (type) {
    case "images":
    case "image":
      return "0";
    case "video":
      return "1";
    case "documents":
      return "2";
    case "audio":
      return "3";
    default:
      return "0";
  }
};

export default function RecentMedia({
  type = "image",
  onClickMedia = () => {},
  showDropdown = true,
}) {
  const wheelId = useSelector(getWheelId);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(showDropdown ? "wheel" : "all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observerRef = useRef(null);

  const fetchMedia = async (reset = false) => {
    if (!hasMore && !reset) return;
    setLoading(true);

    try {
      const response = await getRecentMedia(
        filter === "wheel" ? wheelId : undefined,
        getFileType(type),
        reset ? 1 : page
      );

      if (reset) {
        setMedia(response.data.results);
        setPage(2); // Reset page to 2 after fetching the first page
      } else {
        setMedia((prev) => [...prev, ...response.data.results]);
        setPage((prevPage) => prevPage + 1);
      }

      setHasMore(response.data.results.length > 0); // Stop if no more data
    } catch (err) {
      setHasMore(false)
      setLoading(false)
      console.error("Failed to fetch media:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedia(true); // Reset and fetch first page when type/filter changes
  }, [type, filter]);

  // Scroll observer for infinite scrolling
  const observer = useCallback(
    (node) => {
      if (loading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchMedia();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, hasMore]
  );


  const renderImages = () => {
    const items = media.map(item=>({...item,...item.metadata}))
    return (
      <div>
        <MediaMasonry content={items} onReplaceButtonPress={onClickMedia} />
      </div>
    );
  };

  // Function to render audios
  const renderAudios = (items) => {
    const audios = items.map((item) => ({
      ...item,
      src: item.url,
      title: item.metadata.title,
      embedCode: item.metadata.embedCode,
      embed: !!item.metadata.embedCode,
      image: item.metadata.thumbnail,
      id: item.id,
    }));
    return (
      <>
        {audios.map((item, index) => (
          <MediaContainer key={item.id ?? index}>
            <SelectButton
              type="button"
              className="select-button"
              aria-label={`Select ${item.title || "audio"}`}
              onClick={() => onClickMedia(item)}
            >
              <TextView variant="largeText" color="white">
                Select
              </TextView>
            </SelectButton>
            <AudioBlock audio={item} showIcon={false} />
          </MediaContainer>
        ))}
      </>
    );
  };

  // Function to render videos
  // T22.142 (Kelly 2026-10-12): only the small hover "Select" chip used to
  // pick a video — clicking the thumbnail did nothing. The WHOLE tile is now
  // the button (pointer + Enter/Space); "Select" stays as its visual label.
  const renderVideos = (items) => (
    <Grid>
      {items.map((item, index) => {
        const { url, metadata } = item;
        const select = () =>
          onClickMedia({
            id: item.id,
            src: item.url,
            thumbnail: item.metadata?.thumbnail,
          });
        return (
          <VideoTile
            key={index}
            role="button"
            tabIndex={0}
            aria-label={`Select ${metadata?.title || "video"}`}
            onClick={select}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                e.preventDefault();
                select();
              }
            }}
            style={{ height: 100, backgroundColor: "black" }}
          >
            <SelectButton as="span" className="select-button" aria-hidden="true">
              <TextView variant="largeText" color="white">
                Select
              </TextView>
            </SelectButton>
            {metadata?.type === "youtube" ? (
              <img
                src={metadata?.thumbnail}
                alt="Video thumbnail"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <ReactPlayer
                url={url}
                width="100%"
                height="100%"
                playing={false}
              />
            )}
          </VideoTile>
        );
      })}
    </Grid>
  );

  // Function to render documents
  const renderDocuments = (items) => {
    return (
      <Grid>
        {items.map((item, index) => (
          <DocumentItem key={index}>
            <SelectButton
              type="button"
              className="select-button"
              aria-label={`Select ${item.metadata?.title || "document"}`}
              onClick={() =>
                onClickMedia({
                  id: item.id,
                  src: item.url,
                  title: item.metadata.title,
                  type: item.metadata.type,
                })
              }
            >
              <TextView variant="largeText" color="white">
                Select
              </TextView>
            </SelectButton>
            <DocumentIcon
              src={fileIcons[item.metadata?.type]}
              alt={item.title}
            />
            <DocumentTitle onClick={() => goToUrl(item.url)}>
              {item.metadata?.title}
            </DocumentTitle>
          </DocumentItem>
        ))}
      </Grid>
    );
  };

  // Render based on the type
  const renderMedia = () => {
    switch (getFileType(type)) {
      case "0":
        return renderImages(media);
      case "3":
        return renderAudios(media);
      case "1":
        return renderVideos(media);
      case "2":
        return renderDocuments(media);
      default:
        return <NoMediaMessage>Unsupported media type</NoMediaMessage>;
    }
  };

  return (
    <MediaWrapper>
      {showDropdown && (
        <Dropdown
          variant="outlined"
          label={"Filter by"}
          options={[
            { value: "wheel", label: "This wheel" },
            { value: "all", label: "All wheels" },
          ]}
          containerStyle={{ marginBottom: 20 }}
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            setPage(1); // Reset page when filter changes
          }}
        />
      )}

      {media.length > 0 ? (
        <>
      {renderMedia()}
          <div ref={observer} />
          {!hasMore && !loading && (
            <EndOfResults role="status">You've reached the end</EndOfResults>
          )}
        </>
      ) : !loading ? (
       <NoMediaMessage role="status">
         No recent media here yet. Uploads and embeds you add will show up here
         for quick reuse.
       </NoMediaMessage>
      ) : null}

      {loading && (
        <LoaderContainer>
          <BrandWheelLoader variant="medium" label="Loading recent media…" />
        </LoaderContainer>
      )}
    </MediaWrapper>
  );
}